import type { PropsWithChildren } from "react";

import { useFragment } from "react-relay";
import { useLocation } from "react-router";
import { graphql } from "relay-runtime";

import { PageWithBreadCrumbs } from "app/components/layout/PageWithBreadCrumbs";

import type { AdminPage_query$key } from "./__generated__/AdminPage_query.graphql";

export interface AdminPageProps extends PropsWithChildren {
  queryRef: AdminPage_query$key;
  isForm?: boolean;
  title?: string;
  withPaper?: boolean;
  /** Whether the layout renders `title` as the page heading (H2). Disable for pages that render their own header. */
  withHeader?: boolean;
  /** When set, replaces the admin tab nav with a single parent breadcrumb link */
  parentLink?: { label: string; target: string };
}

export function AdminPage({
  children,
  title = "",
  isForm = false,
  withPaper = true,
  withHeader = true,
  queryRef,
  parentLink,
}: AdminPageProps) {
  const data = useFragment(
    graphql`
      fragment AdminPage_query on Query {
        ...PageWithBreadCrumbs_query
      }
    `,
    queryRef,
  );

  const { pathname } = useLocation();

  const tabs = [
    { label: "Accounts", target: `/admin/accounts` },
    { label: "Bindings", target: `/admin/bindings` },
    { label: "Policies", target: `/admin/policies` },
  ];

  const breadcrumbs = parentLink
    ? [
        { label: "Admin" },
        { label: parentLink.label, target: parentLink.target },
      ]
    : [{ label: "Admin" }, { tabs }];

  const activeTabLabel = tabs.find((tab) =>
    pathname.startsWith(tab.target),
  )?.label;
  // Falls back to the active section when no explicit title is given, so the
  // breadcrumb and browser title (`Admin | …`) stay meaningful on list pages.
  const documentTitle = title || parentLink?.label || activeTabLabel || "Admin";

  return (
    <PageWithBreadCrumbs
      breadcrumbs={breadcrumbs}
      defaultTitle="Admin"
      heading={withHeader ? title : undefined}
      isForm={isForm}
      queryRef={data}
      title={documentTitle}
      withPaper={withPaper}
    >
      {children}
    </PageWithBreadCrumbs>
  );
}
